import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../AuthProvider';
import './ViewTasks.css';
import GmailScreenshotUpload from '../components/GmailScreenshotUpload';
import ConfirmModal from '../components/ConfirmModal';
import SearchableSelect from '../components/SearchableSelect';

function ViewTasks() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  // Restore the date that was active in LeadTasksView (passed via router state), else default to today
  const [selectedDate, setSelectedDate] = useState(
    location.state?.date || new Date().toISOString().split('T')[0]
  );
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [agents, setAgents] = useState([]);
  const [agentSearchTerm, setAgentSearchTerm] = useState('');
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);
  const [tasksData, setTasksData] = useState({
    leads: [],
    summary: {},
    agent: {},
    filter_options: { leads: [], domains: [] },
    pagination: { page: 1, page_size: 20, total_count: 0, total_pages: 1 },
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferFromAgentId, setTransferFromAgentId] = useState('');
  const [transferToAgentId, setTransferToAgentId] = useState('');
  const [transferPendingCount, setTransferPendingCount] = useState(0);
  const [transferError, setTransferError] = useState('');
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferLeadId, setTransferLeadId] = useState(null);
  const [transferLeadName, setTransferLeadName] = useState('');
  const [pendingCountLoading, setPendingCountLoading] = useState(false);
  const [expandedLeads, setExpandedLeads] = useState(new Set());
  const [updatingTask, setUpdatingTask] = useState(null);
  const [expandedScoreDetails, setExpandedScoreDetails] = useState(new Set());
  const [transferToAgentSearchTerm, setTransferToAgentSearchTerm] = useState('');
  const [showTransferToAgentDropdown, setShowTransferToAgentDropdown] = useState(false);
  const [transferReason, setTransferReason] = useState('');
  const [deleteLeadTasksTarget, setDeleteLeadTasksTarget] = useState(null);
  const [isDeletingTasks, setIsDeletingTasks] = useState(false);
  const [selectedLeadFilter, setSelectedLeadFilter] = useState('all');
  const [selectedDomainFilter, setSelectedDomainFilter] = useState('all');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  // Ref for dropdown click outside detection
  const dropdownRef = useRef(null);
  const transferToDropdownRef = useRef(null);

  // Check if user has admin access
  const isAdmin = user && (user.is_superuser || user.role_name === 'Admin' || user.role_name === 'ADMIN' || user.role_name === 'Super Admin' || user.role_name === 'Associate admin' || user.role_name === 'Associate Admin');
  const isOps = user && (user.role_name === 'User' || user.role_name === 'Manager' || user.role_name === 'Associate');

  const fetchAgents = async () => {
    try {
      const response = await fetch('/api/active-ops-users/');
      const data = await response.json();
      setAgents(data.opsUsers || []);
      // Keep default as 'all' - don't auto-select first agent
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError('Failed to load agents');
    }
  };

  const fetchTasks = async (
    abortSignal,
    {
      pageParam = page,
      leadFilter = selectedLeadFilter,
      domainFilter = selectedDomainFilter,
      dateParam = selectedDate,
      agentParam = selectedAgent,
    } = {}
  ) => {
    if (!dateParam) return;

    setLoading(true);
    setError('');
    try {
      let url;
      if (isAdmin) {
        url = `/api/admin/tasks/?date=${dateParam}&page=${pageParam}&page_size=${PAGE_SIZE}`;
        if (agentParam && agentParam !== 'all') {
          url += `&agent_id=${agentParam}`;
        }
      } else if (isOps) {
        url = `/api/tasks/?date=${dateParam}&page=${pageParam}&page_size=${PAGE_SIZE}`;
      } else {
        setError('Access denied: Invalid role');
        return;
      }

      // Server-side lead/domain filters (AND). Do not use lead_id — that triggers detail mode.
      if (leadFilter && leadFilter !== 'all') {
        url += `&filter_lead_id=${encodeURIComponent(leadFilter)}`;
      }
      if (domainFilter && domainFilter !== 'all') {
        url += `&domain=${encodeURIComponent(domainFilter)}`;
      }

      const response = await fetch(url, { signal: abortSignal });
      const data = await response.json();

      if (response.ok) {
        setTasksData({
          leads: data.leads || [],
          summary: data.summary || {},
          agent: data.agent || {},
          filter_options: data.filter_options || { leads: [], domains: [] },
          pagination: data.pagination || {
            page: pageParam,
            page_size: PAGE_SIZE,
            total_count: 0,
            total_pages: 1,
          },
        });
      } else {
        setError(data.error || 'Failed to fetch tasks');
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Error fetching tasks:', err);
      setError('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchAgents();
    }
  }, [isAdmin]);

  // Single fetch for all list params (soft-refresh uses current page + filters)
  useEffect(() => {
    if (!(isAdmin || isOps) || !selectedDate) return;
    const controller = new AbortController();
    fetchTasks(controller.signal, {
      pageParam: page,
      leadFilter: selectedLeadFilter,
      domainFilter: selectedDomainFilter,
      dateParam: selectedDate,
      agentParam: selectedAgent,
    });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, isOps, selectedAgent, selectedDate, page, selectedLeadFilter, selectedDomainFilter]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowAgentDropdown(false);
      }
      if (transferToDropdownRef.current && !transferToDropdownRef.current.contains(event.target)) {
        setShowTransferToAgentDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Filter agents based on search term
  const filteredAgents = agents.filter(agent =>
    agent.name.toLowerCase().includes(agentSearchTerm.toLowerCase()) ||
    agent.email.toLowerCase().includes(agentSearchTerm.toLowerCase())
  );

  // Get selected agent info for display
  const getSelectedAgentDisplay = () => {
    if (selectedAgent === 'all') return 'All Agents';
    const agent = agents.find(a => a.id.toString() === selectedAgent.toString());
    return agent ? `${agent.name} (${agent.email})` : 'Select Agent';
  };

  const handleAgentSelect = (agentId) => {
    setSelectedAgent(agentId);
    setAgentSearchTerm('');
    setShowAgentDropdown(false);
    setSelectedLeadFilter('all');
    setSelectedDomainFilter('all');
    setPage(1);
  };

  const handleLeadFilterChange = (val) => {
    setSelectedLeadFilter(val || 'all');
    setPage(1);
  };

  const handleDomainFilterChange = (val) => {
    setSelectedDomainFilter(val || 'all');
    setPage(1);
  };

  const handleDateSelect = (nextDate) => {
    setSelectedDate(nextDate);
    setSelectedLeadFilter('all');
    setSelectedDomainFilter('all');
    setPage(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowAgentDropdown(false);
      setAgentSearchTerm('');
    }
  };

  const handleDeleteAllTasks = async (leadId) => {
    setIsDeletingTasks(true);
    try {
      const response = await fetch('/api/admin/delete-lead-tasks/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          leadId,
          date: selectedDate,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        alert(data.message);
        setDeleteLeadTasksTarget(null);
        // Refresh tasks data
        fetchTasks();
      } else {
        setError(data.error || 'Failed to delete tasks');
      }
    } catch (err) {
      console.error('Error deleting tasks:', err);
      setError('Failed to delete tasks');
    } finally {
      setIsDeletingTasks(false);
    }
  };

  const getAgentById = (agentId) => {
    if (!agentId) return null;
    return agents.find((agent) => agent.id.toString() === agentId.toString()) || null;
  };

  // Filter agents for "To CA" based on search term, excluding the From Agent if set
  const filteredTransferToAgents = agents.filter(agent => {
    if (transferFromAgentId && agent.id.toString() === transferFromAgentId.toString()) {
      return false;
    }
    return (
      agent.name.toLowerCase().includes(transferToAgentSearchTerm.toLowerCase()) ||
      agent.email.toLowerCase().includes(transferToAgentSearchTerm.toLowerCase())
    );
  });

  const getTransferToAgentDisplay = () => {
    const agent = agents.find(a => a.id.toString() === transferToAgentId.toString());
    return agent ? `${agent.name} (${agent.email})` : 'Select CA';
  };

  const fetchPendingCountForAgent = async (agentId) => {
    if (!agentId || !selectedDate) {
      setTransferPendingCount(0);
      return;
    }

    setPendingCountLoading(true);
    setTransferError('');
    try {
      // summary is full-day for the agent scope; page_size=1 keeps payload tiny
      const response = await fetch(
        `/api/admin/tasks/?date=${selectedDate}&agent_id=${agentId}&page=1&page_size=1`
      );
      const data = await response.json();
      if (response.ok) {
        setTransferPendingCount(data.summary?.pending || 0);
      } else {
        setTransferPendingCount(0);
        setTransferError(data.error || 'Failed to load pending task count');
      }
    } catch (err) {
      console.error('Error loading pending task count:', err);
      setTransferPendingCount(0);
      setTransferError('Failed to load pending task count');
    } finally {
      setPendingCountLoading(false);
    }
  };

  useEffect(() => {
    if (showTransferModal && transferFromAgentId && !transferLeadId) {
      fetchPendingCountForAgent(transferFromAgentId);
    } else if (!showTransferModal) {
      setTransferPendingCount(0);
    }
  }, [showTransferModal, transferFromAgentId, selectedDate, transferLeadId]);

  const openTransferModal = (lead) => {
    setTransferError('');
    setTransferFromAgentId(lead?.agent?.id || '');
    setTransferToAgentId('');
    setTransferToAgentSearchTerm('');
    setShowTransferToAgentDropdown(false);
    setTransferLeadId(lead?.id || null);
    setTransferLeadName(lead?.name || '');
    setTransferPendingCount(lead?.pending || 0);
    setShowTransferModal(true);
  };

  const closeTransferModal = () => {
    setShowTransferModal(false);
    setTransferError('');
    setTransferLoading(false);
    setTransferLeadId(null);
    setTransferLeadName('');
    setTransferFromAgentId('');
    setTransferToAgentId('');
    setTransferToAgentSearchTerm('');
    setShowTransferToAgentDropdown(false);
    setTransferReason('');
  };

  const handleTransferSubmit = async () => {
    setTransferError('');
    if (!transferLeadId) {
      setTransferError('Lead is required for per-lead transfer.');
      return;
    }
    if (!transferFromAgentId || !transferToAgentId) {
      setTransferError('Both From CA and To CA are required.');
      return;
    }
    if (transferFromAgentId === transferToAgentId) {
      setTransferError('From CA and To CA cannot be the same.');
      return;
    }

    const fromAgent = getAgentById(transferFromAgentId);
    const toAgent = getAgentById(transferToAgentId);
    if (!fromAgent || !toAgent) {
      setTransferError('Selected agents are invalid.');
      return;
    }

    setTransferLoading(true);
    try {
      const response = await fetch('/api/admin/task-transfer/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          from_user_id: transferFromAgentId,
          to_user_id: transferToAgentId,
          date: selectedDate,
          lead_id: transferLeadId,
          transfer_reason: transferReason,
        }),
      });

      const data = await response.json();
      if (response.ok && data.success) {
        const successText = `${data.transferred_count} pending tasks successfully transferred from ${fromAgent.name} to ${toAgent.name}.`;
        setShowTransferModal(false);
        fetchTasks();
        setError('');
        setTransferReason('');
        alert(successText);
      } else {
        setTransferError(data.error || 'Unable to transfer pending tasks');
      }
    } catch (err) {
      console.error('Error transferring pending tasks:', err);
      setTransferError('Failed to transfer pending tasks');
    } finally {
      setTransferLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const goToPreviousDay = () => {
    const [y, m, d] = selectedDate.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    date.setDate(date.getDate() - 1);

    const newY = date.getFullYear();
    const newM = String(date.getMonth() + 1).padStart(2, '0');
    const newD = String(date.getDate()).padStart(2, '0');
    handleDateSelect(`${newY}-${newM}-${newD}`);
  };

  const goToNextDay = () => {
    const [y, m, d] = selectedDate.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    date.setDate(date.getDate() + 1);

    const newY = date.getFullYear();
    const newM = String(date.getMonth() + 1).padStart(2, '0');
    const newD = String(date.getDate()).padStart(2, '0');
    handleDateSelect(`${newY}-${newM}-${newD}`);
  };

  const toggleLeadExpansion = (leadId) => {
    const newExpanded = new Set(expandedLeads);
    if (newExpanded.has(leadId)) {
      newExpanded.delete(leadId);
    } else {
      newExpanded.add(leadId);
    }
    setExpandedLeads(newExpanded);
  };

  const toggleScoreDetails = (taskId) => {
    const newExpanded = new Set(expandedScoreDetails);
    if (newExpanded.has(taskId)) {
      newExpanded.delete(taskId);
    } else {
      newExpanded.add(taskId);
    }
    setExpandedScoreDetails(newExpanded);
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    setUpdatingTask(taskId);
    try {
      const response = await fetch('/api/admin/update-task-status/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          taskId,
          status: newStatus,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Refresh tasks data to show updated status
        fetchTasks();
      } else {
        setError(data.error || 'Failed to update task status');
      }
    } catch (err) {
      console.error('Error updating task status:', err);
      setError('Failed to update task status');
    } finally {
      setUpdatingTask(null);
    }
  };

  const getStatusColor = (status) => {
    switch (status.toUpperCase()) {
      case 'PENDING': return 'bg-yellow-200 text-yellow-900';
      case 'IN_PROGRESS':
      case 'STARTED': return 'bg-blue-200 text-blue-900';
      case 'COMPLETED': return 'bg-green-200 text-green-900';
      case 'AWAITING EMAIL': return 'bg-purple-200 text-purple-900';
      case 'ALREADY_APPLIED': return 'bg-teal-200 text-teal-900';
      case 'NOT_RELEVANT': return 'bg-red-200 text-red-900';
      case 'JOB_NOT_FOUND': return 'bg-orange-200 text-orange-900';
      case 'NEED_OTP': return 'bg-pink-200 text-pink-900';
      default: return 'bg-gray-200 text-gray-800';
    }
  };

  const deleteTask = async (taskId) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm('Are you sure you want to delete this task?')) {
      return;
    }

    try {
      const response = await fetch('/api/admin/delete-task/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          taskId,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        alert(data.message);
        // Refresh tasks data
        fetchTasks();
      } else {
        setError(data.error || 'Failed to delete task');
      }
    } catch (err) {
      console.error('Error deleting task:', err);
      setError('Failed to delete task');
    }
  };

  // if (!user || (!isAdmin && !isOps)) {
  //   return (
  //     <div className="min-h-screen bg-gray-50 flex items-center justify-center">
  //       <div className="bg-white p-8 rounded-lg shadow-md">
  //         <h2 className="text-xl font-semibold text-red-600 mb-4">Access Denied</h2>
  //         <p className="text-gray-600">
  //           {!user 
  //             ? "You need to be logged in to view this page." 
  //             : "You need admin or ops privileges to view this page."
  //           }
  //         </p>
  //       </div>
  //     </div>
  //   );
  // }

  // Server-paginated lead cards for the current page
  const leadsList = React.useMemo(() => {
    return tasksData && Array.isArray(tasksData.leads) ? tasksData.leads : [];
  }, [tasksData]);

  // Filter dropdown options from server (leads/domains with tasks for date + agent scope)
  const uniqueLeads = React.useMemo(() => {
    return tasksData?.filter_options?.leads || [];
  }, [tasksData]);

  const uniqueDomains = React.useMemo(() => {
    return tasksData?.filter_options?.domains || [];
  }, [tasksData]);

  // Full-day summary for current filters (not just current page)
  const filteredSummary = React.useMemo(() => {
    return {
      total: 0,
      pending: 0,
      inProgress: 0,
      awaitingEmail: 0,
      completed: 0,
      alreadyApplied: 0,
      notRelevant: 0,
      jobNotFound: 0,
      needOtp: 0,
      completionPercentage: 0,
      ...(tasksData?.summary || {}),
    };
  }, [tasksData]);

  const pagination = tasksData?.pagination || {
    page: 1,
    page_size: PAGE_SIZE,
    total_count: 0,
    total_pages: 1,
  };
  const pageOffset = ((pagination.page || page) - 1) * (pagination.page_size || PAGE_SIZE);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">
            {isAdmin ? "Admin Tasks Dashboard" : "Career Associate Dashboard"}
          </h1>

          {/* Date Selection */}
          <div className="flex items-center justify-center mb-6 space-x-4">
            <button
              onClick={goToPreviousDay}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
            </button>

            <div className="flex flex-col items-center">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Date:
              </label>
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => handleDateSelect(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <button
              onClick={goToNextDay}
              className="p-2 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Filters Section */}
          <div className={`grid grid-cols-1 ${isAdmin ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6 mb-6`}>
            {/* Agent Filter - Only for Admin */}
            {isAdmin && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter by Agent:
                </label>
                <div className="relative w-full" ref={dropdownRef}>
                  <div
                    onClick={() => setShowAgentDropdown(!showAgentDropdown)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white cursor-pointer focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 flex justify-between items-center"
                  >
                    <span className="truncate">{getSelectedAgentDisplay()}</span>
                    <svg
                      className={`w-5 h-5 text-gray-400 transition-transform ${showAgentDropdown ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>

                  {showAgentDropdown && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                      <div className="p-2 border-b">
                        <input
                          type="text"
                          placeholder="Search agents..."
                          value={agentSearchTerm}
                          onChange={(e) => setAgentSearchTerm(e.target.value)}
                          onKeyDown={handleKeyDown}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                        />
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        <div
                          onClick={() => handleAgentSelect('all')}
                          className={`px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm ${selectedAgent === 'all' ? 'bg-indigo-50 text-indigo-600 font-medium' : ''}`}
                        >
                          All Agents
                        </div>
                        {filteredAgents.map(agent => (
                          <div
                            key={agent.id}
                            onClick={() => handleAgentSelect(agent.id)}
                            className={`px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm ${selectedAgent.toString() === agent.id.toString() ? 'bg-indigo-50 text-indigo-600 font-medium' : ''}`}
                          >
                            <div className="font-medium">{agent.name}</div>
                            <div className="text-xs text-gray-500">{agent.email}</div>
                          </div>
                        ))}
                        {filteredAgents.length === 0 && agentSearchTerm && (
                          <div className="px-3 py-2 text-gray-500 text-xs">
                            No agents found matching "{agentSearchTerm}"
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Lead Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Lead:
              </label>
              <SearchableSelect
                options={[
                  { value: 'all', label: 'All Leads' },
                  ...uniqueLeads.map(lead => ({
                    value: lead.id,
                    label: `${lead.name} (${lead.email})`
                  }))
                ]}
                value={selectedLeadFilter}
                onChange={handleLeadFilterChange}
                placeholder="Select lead..."
              />
            </div>

            {/* Domain Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Domain:
              </label>
              <SearchableSelect
                options={[
                  { value: 'all', label: 'All Domains' },
                  ...uniqueDomains.map(domain => ({
                    value: domain,
                    label: domain
                  }))
                ]}
                value={selectedDomainFilter}
                onChange={handleDomainFilterChange}
                placeholder="Select domain..."
              />
            </div>
          </div>


          {/* View Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
            <p className="text-blue-800 text-sm">
              {isAdmin
                ? "Admin View: Tasks grouped by ops agents for selected date"
                : isOps
                  ? "Career Associate Dashboard"
                  : "Task Management Dashboard"
              }
            </p>
          </div>
        </div>

        {showTransferModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 py-6">
            <div className="w-full max-w-2xl bg-white rounded-xl shadow-xl overflow-hidden">
              <div className="bg-gray-100 px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">Transfer Pending Tasks</h2>
                <button onClick={closeTransferModal} className="text-gray-600 hover:text-gray-900">×</button>
              </div>

              <div className="px-6 py-6 space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">From CA</label>
                    {transferLeadId ? (
                      <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100">
                        {getAgentById(transferFromAgentId)?.name || 'Unassigned'} {getAgentById(transferFromAgentId)?.email ? `(${getAgentById(transferFromAgentId)?.email})` : ''}
                      </div>
                    ) : (
                      <select
                        value={transferFromAgentId}
                        onChange={(e) => setTransferFromAgentId(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Select CA</option>
                        {agents.map((agent) => (
                          <option key={agent.id} value={agent.id}>{agent.name} ({agent.email})</option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">To CA</label>
                    <div className="relative w-full text-left" ref={transferToDropdownRef}>
                      <div
                        onClick={() => setShowTransferToAgentDropdown(!showTransferToAgentDropdown)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white cursor-pointer focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 flex justify-between items-center"
                      >
                        <span className="truncate">{getTransferToAgentDisplay()}</span>
                        <svg
                          className={`w-5 h-5 text-gray-400 transition-transform ${showTransferToAgentDropdown ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>

                      {showTransferToAgentDropdown && (
                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
                          <div className="p-2 border-b">
                            <input
                              type="text"
                              placeholder="Search agents..."
                              value={transferToAgentSearchTerm}
                              onChange={(e) => setTransferToAgentSearchTerm(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Escape') {
                                  setShowTransferToAgentDropdown(false);
                                  setTransferToAgentSearchTerm('');
                                }
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                              onClick={(e) => e.stopPropagation()}
                              autoFocus
                            />
                          </div>
                          <div className="max-h-48 overflow-y-auto">
                            <div
                              onClick={() => {
                                setTransferToAgentId('');
                                setTransferToAgentSearchTerm('');
                                setShowTransferToAgentDropdown(false);
                              }}
                              className={`px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm ${!transferToAgentId ? 'bg-indigo-50 text-indigo-600 font-medium' : ''}`}
                            >
                              Select CA
                            </div>
                            {filteredTransferToAgents.map(agent => (
                              <div
                                key={agent.id}
                                onClick={() => {
                                  setTransferToAgentId(agent.id);
                                  setTransferToAgentSearchTerm('');
                                  setShowTransferToAgentDropdown(false);
                                }}
                                className={`px-3 py-2 cursor-pointer hover:bg-gray-100 text-sm ${transferToAgentId.toString() === agent.id.toString() ? 'bg-indigo-50 text-indigo-600 font-medium' : ''}`}
                              >
                                <div className="font-medium">{agent.name}</div>
                                <div className="text-xs text-gray-500">{agent.email}</div>
                              </div>
                            ))}
                            {filteredTransferToAgents.length === 0 && (
                              <div className="px-3 py-2 text-gray-500 text-xs">
                                {transferToAgentSearchTerm ? `No agents found matching "${transferToAgentSearchTerm}"` : 'No other agents available'}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                  <p className="text-sm text-gray-600 mb-2">Pending Task Count for this lead:</p>
                  <div className="flex items-center justify-between">
                    <span className="text-2xl font-semibold text-gray-900">
                      {pendingCountLoading ? 'Loading...' : transferPendingCount}
                    </span>
                    <span className="text-sm text-gray-500">Date: {formatDate(selectedDate)}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Reason for Transfer (Optional)</label>
                  <input
                    type="text"
                    value={transferReason}
                    onChange={(e) => setTransferReason(e.target.value)}
                    placeholder="e.g. CA is absent, Work load balance"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                  />
                </div>

                <div className="text-sm text-gray-700">
                  {transferFromAgentId && transferToAgentId && (
                    <p>
                      Are you sure you want to transfer all pending tasks for
                      <strong>{transferLeadName || 'this lead'}</strong> from
                      <strong>{getAgentById(transferFromAgentId)?.name || 'From CA'}</strong> to
                      <strong>{getAgentById(transferToAgentId)?.name || 'To CA'}</strong>?
                    </p>
                  )}
                  {!transferFromAgentId || !transferToAgentId ? (
                    <p className="text-gray-500">Please select both CAs before confirming.</p>
                  ) : null}
                </div>

                {transferError && (
                  <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
                    {transferError}
                  </div>
                )}
              </div>

              <div className="bg-gray-50 px-6 py-4 flex flex-col gap-3 sm:flex-row sm:justify-end">
                <button
                  onClick={closeTransferModal}
                  className="w-full sm:w-auto px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  onClick={handleTransferSubmit}
                  disabled={transferLoading || !transferFromAgentId || !transferToAgentId}
                  className="w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {transferLoading ? 'Transferring...' : 'Confirm'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <p className="text-gray-600">Loading tasks...</p>
          </div>
        )}

        {/* Tasks Summary */}
        {tasksData && tasksData.summary && (
          <div className="bg-white rounded-lg shadow p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Task Summary</h2>
            <div className="grid grid-cols-2 md:grid-cols-9 gap-4">
              <div className="bg-gray-200 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-gray-900">{filteredSummary.total}</div>
                <div className="text-sm text-gray-700">Total Tasks</div>
              </div>
              <div className="bg-yellow-200 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-yellow-900">{filteredSummary.pending}</div>
                <div className="text-sm text-yellow-800">Pending</div>
              </div>
              <div className="bg-blue-200 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-blue-900">{filteredSummary.inProgress}</div>
                <div className="text-sm text-blue-800">In Progress</div>
              </div>
              <div className="bg-purple-200 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-purple-900">{filteredSummary.awaitingEmail}</div>
                <div className="text-sm text-purple-800">Awaiting Email</div>
              </div>
              <div className="bg-green-200 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-green-900">{filteredSummary.completed}</div>
                <div className="text-sm text-green-800">Completed</div>
              </div>
              <div className="bg-teal-200 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-teal-900">{filteredSummary.alreadyApplied}</div>
                <div className="text-sm text-teal-800">Already Applied</div>
              </div>
              <div className="bg-red-200 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-red-900">{filteredSummary.notRelevant}</div>
                <div className="text-sm text-red-800">Not Relevant</div>
              </div>
              <div className="bg-orange-200 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-orange-900">{filteredSummary.jobNotFound}</div>
                <div className="text-sm text-orange-800">Job Not Found</div>
              </div>
              <div className="bg-pink-200 p-4 rounded-lg text-center">
                <div className="text-2xl font-bold text-pink-900">{filteredSummary.needOtp}</div>
                <div className="text-sm text-pink-800">Need OTP/Auth</div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-4">
              <div className="flex justify-between text-sm text-gray-600 mb-1">
                <span>Completion Progress</span>
                <span>{filteredSummary.completionPercentage}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-green-500 h-3 rounded-full"
                  style={{ width: `${filteredSummary.completionPercentage}%` }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Lead Tasks */}
        {leadsList.length > 0 && (
          <div className="space-y-4">
            {leadsList.map((lead, index) => (
              <div key={lead.id || index} className="bg-white rounded-lg shadow overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-4">
                      <button
                        className="text-blue-500 hover:text-blue-700 transition-colors"
                        onClick={() => {
                          navigate(`/lead-tasks/${lead.id}`, { state: { lead, selectedDate } });
                        }}
                        title="View All Tasks for this Lead"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{pageOffset + index + 1}. {lead.name}</h3>
                        <p className="text-sm text-gray-600">{lead.email}</p>
                        {lead.targetRole && (
                          <p className="text-xs text-gray-500 font-medium mt-0.5">
                            Domain: {lead.targetRole}
                          </p>
                        )}
                        {lead.endDate && (
                          <p className="text-xs text-red-600 font-semibold">
                            End Date: {formatDate(lead.endDate)}
                          </p>
                        )}
                        {selectedAgent === 'all' && lead.agent && (
                          <p className="text-xs text-blue-600">
                            Agent: {lead.agent.name} ({lead.agent.email})
                          </p>
                        )}
                      </div>
                    </div>
                    {isAdmin && (
                      <div className="flex gap-2">
                        {lead.pending > 0 && (
                          <button
                            onClick={() => openTransferModal(lead)}
                            className="px-4 py-2 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700"
                          >
                            Transfer Pending Tasks
                          </button>
                        )}
                        {user.role_name !== 'Associate admin' && user.role_name !== 'Associate Admin' && (
                          <button
                            onClick={() => setDeleteLeadTasksTarget(lead)}
                            className="px-4 py-2 bg-red-500 text-white text-sm rounded hover:bg-red-600"
                          >
                            Delete All Tasks for Lead (on this date)
                          </button>
                        )}
                      </div>
                    )}
                    {/* {!isAdmin && (
                      <GmailScreenshotUpload
                        leadId={lead.id}
                        leadName={lead.name}
                        selectedDate={selectedDate}
                      />
                    )} */}
                  </div>

                  {/* Task Stats for this lead */}
                  <div className="grid grid-cols-4 md:grid-cols-9 gap-4 mb-4">
                    <div className="bg-gray-200 p-3 rounded-lg text-center">
                      <div className="text-lg font-semibold text-gray-900">{lead.total}</div>
                      <div className="text-xs text-gray-700">Total</div>
                    </div>
                    <div className="bg-yellow-200 p-3 rounded-lg text-center">
                      <div className="text-lg font-semibold text-yellow-900">{lead.pending}</div>
                      <div className="text-xs text-yellow-800">Pending</div>
                    </div>
                    <div className="bg-blue-200 p-3 rounded-lg text-center">
                      <div className="text-lg font-semibold text-blue-900">{lead.inProgress}</div>
                      <div className="text-xs text-blue-800">In Progress</div>
                    </div>
                    <div className="bg-purple-200 p-3 rounded-lg text-center">
                      <div className="text-lg font-semibold text-purple-900">{lead.awaitingEmail || 0}</div>
                      <div className="text-xs text-purple-800">Awaiting Email</div>
                    </div>
                    <div className="bg-green-200 p-3 rounded-lg text-center">
                      <div className="text-lg font-semibold text-green-900">{lead.completed}</div>
                      <div className="text-xs text-green-800">Completed</div>
                    </div>
                    <div className="bg-teal-200 p-3 rounded-lg text-center">
                      <div className="text-lg font-semibold text-teal-900">{lead.alreadyApplied || 0}</div>
                      <div className="text-xs text-teal-800">Already Applied</div>
                    </div>
                    <div className="bg-red-200 p-3 rounded-lg text-center">
                      <div className="text-lg font-semibold text-red-900">{lead.notRelevant || 0}</div>
                      <div className="text-xs text-red-800">Not Relevant</div>
                    </div>
                    <div className="bg-orange-200 p-3 rounded-lg text-center">
                      <div className="text-lg font-semibold text-orange-900">{lead.jobNotFound || 0}</div>
                      <div className="text-xs text-orange-800">Job Not Found</div>
                    </div>
                    <div className="bg-pink-200 p-3 rounded-lg text-center">
                      <div className="text-lg font-semibold text-pink-900">{lead.needOtp || 0}</div>
                      <div className="text-xs text-pink-800">Need OTP/Auth</div>
                    </div>
                  </div>

                  {/* Progress Bar for this lead */}
                  <div className="mb-4">
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Completion Progress</span>
                      <span>{lead.total > 0 ? Math.round(((lead.completed + (lead.awaitingEmail || 0) + lead.alreadyApplied + lead.notRelevant + lead.jobNotFound) / lead.total) * 100) : 0}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full transition-all duration-300"
                        style={{
                          width: `${lead.total > 0 ? Math.round(((lead.completed + (lead.awaitingEmail || 0) + lead.alreadyApplied + lead.notRelevant + lead.jobNotFound) / lead.total) * 100) : 0}%`
                        }}
                      />
                    </div>
                  </div>

                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.total_count > 0 && (
          <div className="bg-white rounded-lg shadow px-6 py-4 mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-sm text-gray-600">
              Showing {pageOffset + 1}-{Math.min(pageOffset + leadsList.length, pagination.total_count)} of {pagination.total_count} leads
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700 px-2">
                Page {pagination.page || page} of {pagination.total_pages || 1}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pagination.total_pages || 1, p + 1))}
                disabled={page >= (pagination.total_pages || 1) || loading}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}

        {/* No Tasks / Filtered Out Message */}
        {!loading && tasksData && (pagination.total_count === 0 ? (
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <p className="text-gray-600">
              {(selectedLeadFilter !== 'all' || selectedDomainFilter !== 'all')
                ? 'No tasks match the active Lead or Domain filters.'
                : 'No tasks found for the selected agent and date.'}
            </p>
          </div>
        ) : null)}
      </div>
      <ConfirmModal
        isOpen={!!deleteLeadTasksTarget}
        title="Delete Lead Tasks"
        message={`Are you sure you want to delete all tasks for ${
          deleteLeadTasksTarget ? deleteLeadTasksTarget.name : 'this lead'
        } on this date?`}
        confirmText="Delete All"
        cancelText="Cancel"
        loading={isDeletingTasks}
        onConfirm={() => handleDeleteAllTasks(deleteLeadTasksTarget.id)}
        onCancel={() => setDeleteLeadTasksTarget(null)}
        danger={true}
      />
    </div >
  );
}

export default ViewTasks;
